import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ChatMessage as ChatMessageType } from '../../store/aiAssistantSlice';
import { aiAssistantActions } from '../../store/aiAssistantSlice';
import { selectIsStreaming } from '../../store/selectors';
import {
  parseAssistantMessage,
  parseStreamingMessage
} from '../../services/responseParser';
import { selectMessages, selectConversationId } from '../../store/selectors';
import { submitFeedback } from '../../services/feedbackService';
import { AssistantSuggestionIcon } from './AssistantSuggestionIcon';
import { AssistantMessageBubble } from './AssistantMessageBubble';
import { FeedbackDialog } from './FeedbackDialog';

interface Props {
  message: ChatMessageType;
}

type FeedbackVote = 'up' | 'down' | null;

function ThumbUpIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 18 18"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="nonzero"
        d="M 16.5302791595459 6.665040493011475 C 16.019049167633057 5.898440480232239 15.163579404354095 5.440430164337158 14.242189407348633 5.440430164337158 L 11.197269439697266 5.440430164337158 C 10.810059428215027 5.440430164337158 10.481939125806093 5.137700259685516 10.450199127197266 4.751950263977051 L 10.25 2.372069835662842 C 10.2519499999471 2.3203098364174366 10.248049720190465 2.234369933605194 10.239259719848633 2.1835899353027344 C 10.127929717302322 1.5302699208259583 9.866699457168579 0 8.064939498901367 0 C 6.583009481430054 0 6.275390207767487 1.6689502000808716 5.978030204772949 3.284180164337158 C 5.63819020986557 5.130860209465027 5.3198301792144775 6.437500164378434 4.317870140075684 6.440430164337158 L 2.25 6.440430164337158 C 1.0092799663543701 6.440430164337158 0 7.450199723243713 0 8.6904296875 L 0 14.1904296875 C 0 15.430659651756287 1.0092799663543701 16.4404296875 2.25 16.4404296875 L 9.75 16.4404296875 C 9.784180000424385 16.4404296875 9.817869752645493 16.438479935284704 9.850589752197266 16.433589935302734 L 11.290529251098633 16.433589935302734 C 12.80907928943634 16.43260993529111 14.166990160942078 15.525389671325684 14.750490188598633 14.124019622802734 L 16.780759811401367 9.247069358825684 C 17.135249823331833 8.396479368209839 17.04150915145874 7.4316405057907104 16.5302791595459 6.665040493011475 Z M 1.5000100135803223 14.1904296875 L 1.5000100135803223 8.6904296875 C 1.5000100135803223 8.277339696884155 1.8364400267601013 7.940430164337158 2.2500100135803223 7.940430164337158 L 3.2500100135803223 7.940430164337158 L 3.2500100135803223 14.9404296875 L 2.2500100135803223 14.9404296875 C 1.8364400267601013 14.9404296875 1.5000100135803223 14.603519678115845 1.5000100135803223 14.1904296875 Z M 15.39600944519043 8.670899391174316 L 13.365739822387695 13.547849655151367 C 13.015639811754227 14.388669669628143 12.200700044631958 14.932619935309049 11.290060043334961 14.933589935302734 L 9.750019073486328 14.933589935302734 C 9.715839073061943 14.933589935302734 9.682149320840836 14.93553968751803 9.649429321289062 14.9404296875 L 4.7500200271606445 14.9404296875 L 4.7500200271606445 7.906980037689209 C 6.705829977989197 7.606930047273636 7.134969979524612 5.283200263977051 7.453629970550537 3.5556602478027344 C 7.57618997246027 2.888670265674591 7.805190473794937 1.6464801132678986 8.064960479736328 1.5000001192092896 C 8.42580047249794 1.5000001192092896 8.594259262084961 1.5000001788139343 8.750509262084961 2.377930164337158 C 8.750999262090772 2.3925801645964384 8.751490479742642 2.407230081036687 8.752460479736328 2.418950080871582 L 8.955099105834961 4.875 C 9.050309106707573 6.033200025558472 10.035180449485779 6.940430164337158 11.197290420532227 6.940430164337158 L 14.242210388183594 6.940430164337158 C 14.667500376701355 6.940430164337158 15.046409457921982 7.143549829721451 15.282249450683594 7.497069835662842 C 15.518089443445206 7.850589841604233 15.559579446911812 8.278319388628006 15.39600944519043 8.670899391174316 Z"
      />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 18 18"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="nonzero"
        d="M 16.780759811401367 7.194329738616943 L 14.750490188598633 2.317379951477051 C 14.16504019498825 0.9111299514770508 12.807129979133606 0.0009699999936856329 11.290040016174316 0 L 2.25 0 C 1.0092799663543701 0 0 1.0097699165344238 0 2.2509799003601074 L 0 7.750979900360107 C 0 8.991209864616394 1.0092799663543701 10.000980377197266 2.25 10.000980377197266 L 4.315919876098633 10.000980377197266 C 5.305669903755188 10.00391037715599 5.60205015540123 11.247069358825684 5.940430164337158 13.144529342651367 C 6.22119015455246 14.717769384384155 6.539060473442078 16.5 8.064940452575684 16.5 C 9.915530443191528 16.5 10.127929672598839 15.029301285743713 10.244139671325684 14.224611282348633 C 10.245599671383388 14.212891282513738 10.450189590454102 11.689449310302734 10.450189590454102 11.689449310302734 C 10.48192958906293 11.303709298372269 10.810049891471863 11.000970840454102 11.197259902954102 11.000970840454102 L 14.242179870605469 11.000970840454102 C 15.163569867610931 11.000970840454102 16.019039630889893 10.542959570884705 16.530269622802734 9.776359558105469 C 17.041499614715576 9.009759545326233 17.135249823331833 8.044909715652466 16.780759811401367 7.194329738616943 Z M 1.5 7.750970363616943 L 1.5 2.250969886779785 C 1.5 1.8369098901748657 1.836430013179779 1.4999899864196777 2.25 1.4999899864196777 L 3.25 1.4999899864196777 L 3.25 8.500970840454102 L 2.25 8.500970840454102 C 1.836430013179779 8.500970840454102 1.5 8.164060354232788 1.5 7.750970363616943 Z M 15.282230377197266 8.944330215454102 C 15.046390384435654 9.297850221395493 14.667479395866394 9.500970840454102 14.242189407348633 9.500970840454102 L 11.197270393371582 9.500970840454102 C 10.035160422325134 9.500970840454102 9.050300031900406 10.408200860023499 8.955080032348633 11.567380905151367 C 8.955080032348633 11.567380905151367 8.760259639006108 13.981440246105194 8.75780963897705 14.024410247802734 C 8.616699635982513 15.000000238418579 8.49852979183197 14.986321153119206 8.044429779052734 14.998041152954102 C 7.775879770517349 14.891591154038906 7.53369028121233 13.534169852733612 7.416990280151367 12.880849838256836 C 7.109490275382996 11.156239867210388 6.694460034370422 8.837150514125824 4.75 8.535150527954102 L 4.75 1.4999899864196777 L 11.289549827575684 1.4999899864196777 C 12.198729813098907 1.5009699864313006 13.013669788837433 2.047839879989624 13.36571979522705 2.8935399055480957 L 15.395990371704102 7.7704901695251465 C 15.559560373425484 8.163070172071457 15.518070369958878 8.59081020951271 15.282230377197266 8.944330215454102 Z"
      />
    </svg>
  );
}

export function ChatMessage({ message }: Props) {
  const dispatch = useDispatch();
  const isStreamingGlobal = useSelector(selectIsStreaming);
  const allMessages = useSelector(selectMessages);
  const conversationId = useSelector(selectConversationId);

  const [vote, setVote] = useState<FeedbackVote>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const isUser = message.role === 'user';
  const isStreaming = message.status === 'streaming';
  const isError = message.status === 'error';
  const isThinking = isStreaming && !message.content;

  const handleSuggestion = useCallback(
    (text: string) => {
      if (!isStreamingGlobal) dispatch(aiAssistantActions.sendMessage(text));
    },
    [dispatch, isStreamingGlobal]
  );

  const msgIndex = allMessages.findIndex(m => m.id === message.id);
  const precedingUserMsg =
    msgIndex > 0
      ? [...allMessages]
          .slice(0, msgIndex)
          .reverse()
          .find(m => m.role === 'user')
      : undefined;
  const onRetry =
    precedingUserMsg && !isStreamingGlobal
      ? () => dispatch(aiAssistantActions.sendMessage(precedingUserMsg.content))
      : undefined;

  const completeParsed = useMemo(
    () =>
      !isUser && !isStreaming ? parseAssistantMessage(message.content) : null,
    [isUser, isStreaming, message.content]
  );
  const streamParsed = useMemo(
    () =>
      !isUser && isStreaming ? parseStreamingMessage(message.content) : null,
    [isUser, isStreaming, message.content]
  );

  const body = completeParsed?.body ?? streamParsed?.body ?? message.content;
  const brief = completeParsed?.brief ?? streamParsed?.brief ?? null;
  const pagination =
    completeParsed?.pagination ?? streamParsed?.pagination ?? null;
  const suggestions = completeParsed?.suggestions ?? [];

  const hasAssistantBrief = Boolean(brief);
  const hasMore = pagination !== null && pagination.total > pagination.showing;

  const handleThumbUp = () => {
    if (vote === 'up') return;
    setVote('up');
    if (conversationId && message.requestId) {
      submitFeedback({
        conversationId,
        requestId: message.requestId,
        rating: 1
      }).catch(() => {});
    }
  };

  const handleThumbDown = () => {
    if (vote === 'down') {
      setVote(null);
    } else {
      setVote('down');
      setShowFeedbackDialog(true);
    }
  };

  const handleFeedbackSubmit = (text: string) => {
    setShowFeedbackDialog(false);
    if (conversationId && message.requestId) {
      submitFeedback({
        conversationId,
        requestId: message.requestId,
        rating: 0,
        comment: text || undefined
      }).catch(() => {});
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedbackDialog(false);
    if (vote === 'down') setVote(null);
  };

  if (isUser) {
    return (
      <div className="rau-message rau-message--user">
        <div className="rau-bubble rau-bubble--user">
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  const feedbackBar = !isStreaming && !isError && (
    <div className="rau-feedback-bar">
      <button
        type="button"
        className={`rau-feedback-btn${
          vote === 'up' ? ' rau-feedback-btn--active' : ''
        }`}
        aria-label="Thumbs up"
        aria-pressed={vote === 'up'}
        onClick={handleThumbUp}
      >
        <ThumbUpIcon />
      </button>
      <button
        type="button"
        className={`rau-feedback-btn${
          vote === 'down' ? ' rau-feedback-btn--active' : ''
        }`}
        aria-label="Thumbs down"
        aria-pressed={vote === 'down'}
        onClick={handleThumbDown}
      >
        <ThumbDownIcon />
      </button>
      {showFeedbackDialog && (
        <FeedbackDialog
          onClose={handleFeedbackClose}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );

  const suggestionsSection = !isStreaming &&
    !isError &&
    suggestions.length > 0 && (
      <div className="rau-follow-up">
        {suggestions.map(s => (
          <button
            key={s}
            type="button"
            className="rau-suggestion"
            disabled={isStreamingGlobal}
            onClick={() => handleSuggestion(s)}
          >
            <span className="rau-suggestion-text">{s}</span>
          </button>
        ))}
      </div>
    );

  const bubble = (
    <AssistantMessageBubble
      body={body}
      isStreaming={isStreaming}
      isThinking={isThinking}
      isError={isError}
      hasMore={hasMore}
      thinkingText={message.thinkingText}
      onAskMore={() => handleSuggestion('Show the next 10 results')}
      onRetry={onRetry}
    />
  );

  if (hasAssistantBrief && brief) {
    return (
      <div className="rau-message rau-message--assistant rau-message--with-brief">
        <div className="rau-assistant-stack">
          <div className="rau-response-brief" title={brief}>
            <span className="rau-response-brief__icon" aria-hidden="true">
              <AssistantSuggestionIcon width={20} height={20} />
            </span>
            <span className="rau-response-brief__text">{brief}</span>
          </div>
          {bubble}
          {feedbackBar}
          {suggestionsSection}
        </div>
      </div>
    );
  }

  return (
    <div className="rau-message rau-message--assistant">
      <div className="rau-avatar rau-avatar--assistant" aria-hidden="true">
        <AssistantSuggestionIcon width={16} height={16} />
      </div>
      <div className="rau-assistant-stack">
        {bubble}
        {feedbackBar}
        {suggestionsSection}
      </div>
    </div>
  );
}
